import { BroadcastContentType, TimeSinceLastUpdate } from '@ardoq/api-types';

export const PEOPLE_WORKSPACE_ID_QUERY = `g.V().hasLabel('Person').values('rootWorkspace').dedup()`;
export const DEFAULT_TIME_SINCE_LAST_UPDATE = TimeSinceLastUpdate.THREE_MONTHS;

export const SURVEY_READONLY_PLACEHOLDER =
  '\n\n{color:rgb(58, 5, 58)/}\\{surveyLinks\\}{color}';

export const MESSAGE_READONLY_PLACEHOLDER =
  '\n\n{color:rgb(58, 5, 58)/}\\{componentList\\}{color}';

export const REPORT_READONLY_PLACEHOLDER =
  '\n\n{color:rgb(58, 5, 58)/}\\{linkToReport\\}{color}';

type BroadcastMessageDefault = {
  subject: string;
  body: string;
};

type DefaultsByContentType = Record<
  BroadcastContentType,
  BroadcastMessageDefault
>;

const EN_DEFAULTS: DefaultsByContentType = {
  [BroadcastContentType.SURVEY]: {
    subject: 'Invitation to Ardoq Survey(s)',
    body: '# {color:rgb(58, 5, 58)/}Please Contribute to our Ardoq Survey{color}\n\nHi \\{audienceName\\},\n\nYou are invited to update information for \\{componentCount\\} \\{componentType\\}\\(s\\) in our \\{surveyName\\}.\n\nClick on the link\\(s\\) below to go directly to the survey.',
  },
  [BroadcastContentType.MESSAGE]: {
    subject: 'Message about {componentType}',
    body: '# {color:rgb(58, 5, 58)/}\\{componentType\\} Alert{color}\n\nHi \\{audienceName\\},\n\nThis message is to let you know that the \\{componentType\\}\\(s\\) below have \\(specify your filter condition here\\).',
  },
  [BroadcastContentType.REPORT]: {
    subject:
      'Alert about {reportName}: {Column} {Aggregate} of {Value} is {Condition} {Threshold}',
    body: '# {color:rgb(58, 5, 58)/}Alert about \\{reportName\\}{color}\n\nHi \\{audienceName\\},\n\nThis is to notify you that a column in the report \\{reportName\\} has reached the set threshold.\n\nThe **\\{Aggregate\\}** of **\\{Column\\}** is **\\{Value\\}**, which is **\\{Condition\\}** the threshold of **\\{Threshold\\}**.\n\n You can click the link below to see the details of the report.',
  },
};

const DE_DEFAULTS: DefaultsByContentType = {
  [BroadcastContentType.SURVEY]: {
    subject: 'Einladung zu Ardoq-Umfrage(n)',
    body: '# {color:rgb(58, 5, 58)/}Bitte trage zu unserer Ardoq-Umfrage bei{color}\n\nHallo \\{audienceName\\},\n\ndu bist eingeladen, Informationen zu \\{componentCount\\} \\{componentType\\}\\(s\\) in unserer \\{surveyName\\} zu aktualisieren.\n\nKlicke auf den/die untenstehenden Link\\(s\\), um direkt zur Umfrage zu gelangen.',
  },
  [BroadcastContentType.MESSAGE]: {
    subject: 'Nachricht zu {componentType}',
    body: '# {color:rgb(58, 5, 58)/}Hinweis zu \\{componentType\\}{color}\n\nHallo \\{audienceName\\},\n\ndiese Nachricht informiert dich darüber, dass die unten aufgeführten \\{componentType\\}\\(s\\) \\(hier deine Filterbedingung angeben\\).',
  },
  [BroadcastContentType.REPORT]: {
    subject:
      'Hinweis zu {reportName}: {Column} {Aggregate} von {Value} ist {Condition} {Threshold}',
    body: '# {color:rgb(58, 5, 58)/}Hinweis zu \\{reportName\\}{color}\n\nHallo \\{audienceName\\},\n\ndies ist eine Benachrichtigung, dass eine Spalte im Bericht \\{reportName\\} den festgelegten Schwellenwert erreicht hat.\n\nDer/die **\\{Aggregate\\}** von **\\{Column\\}** beträgt **\\{Value\\}**, was **\\{Condition\\}** dem Schwellenwert von **\\{Threshold\\}** entspricht.\n\n Klicke auf den Link unten, um die Berichtsdetails einzusehen.',
  },
};

const FR_DEFAULTS: DefaultsByContentType = {
  [BroadcastContentType.SURVEY]: {
    subject: 'Invitation à enquête(s) Ardoq',
    body: "# {color:rgb(58, 5, 58)/}Veuillez contribuer à notre enquête Ardoq{color}\n\nBonjour \\{audienceName\\},\n\nVous êtes invité·e à mettre à jour les informations de \\{componentCount\\} \\{componentType\\}\\(s\\) dans notre \\{surveyName\\}.\n\nCliquez sur le\\(s\\) lien\\(s\\) ci-dessous pour accéder directement à l'enquête.",
  },
  [BroadcastContentType.MESSAGE]: {
    subject: 'Message à propos de {componentType}',
    body: '# {color:rgb(58, 5, 58)/}Alerte \\{componentType\\}{color}\n\nBonjour \\{audienceName\\},\n\nCe message vous informe que les \\{componentType\\}\\(s\\) ci-dessous ont \\(précisez votre condition de filtre ici\\).',
  },
  [BroadcastContentType.REPORT]: {
    subject:
      'Alerte concernant {reportName} : {Column} {Aggregate} de {Value} est {Condition} {Threshold}',
    body: "# {color:rgb(58, 5, 58)/}Alerte concernant \\{reportName\\}{color}\n\nBonjour \\{audienceName\\},\n\nCeci vous informe qu'une colonne du rapport \\{reportName\\} a atteint le seuil défini.\n\n**\\{Aggregate\\}** de **\\{Column\\}** est **\\{Value\\}**, ce qui est **\\{Condition\\}** le seuil de **\\{Threshold\\}**.\n\n Cliquez sur le lien ci-dessous pour voir les détails du rapport.",
  },
};

const DEFAULTS_BY_LANGUAGE = {
  en: EN_DEFAULTS,
  de: DE_DEFAULTS,
  fr: FR_DEFAULTS,
} as const satisfies Record<string, DefaultsByContentType>;

export const SUPPORTED_DEFAULT_LANGUAGES = Object.keys(
  DEFAULTS_BY_LANGUAGE
) as (keyof typeof DEFAULTS_BY_LANGUAGE)[];

export const getDefaultBroadcastMessage = (
  i18nLanguage: string,
  contentType: BroadcastContentType
): BroadcastMessageDefault =>
  (DEFAULTS_BY_LANGUAGE[i18nLanguage as keyof typeof DEFAULTS_BY_LANGUAGE] ??
    EN_DEFAULTS)[contentType];

export const BROADCAST_BUILDER_NAMESPACE = 'broadcast builder';

export const DEFAULT_GREMLIN_AUDIENCE_DIALOG_QUERY = `g.V().hasId(ids).both().hasLabel('Person').path()`;

export const MAX_VISIBLE_QUERY_OPTIONS = 6;

export const HAS_MORE_ID = 'has-more';
